import type { MemberAccess, MemberLayout, Section, TaskStatus } from './types';

/**
 * The same vocabulary the server enforces, mirrored here so the UI can draw the
 * right chrome without a round trip. It is only ever a rendering hint: every
 * answer this file gives is re-decided by the API, which is the one that counts.
 */
export const SECTIONS: Section[] = ['board', 'activity', 'releases'];

export const ABILITIES = [
  'task.create',
  'task.claim',
  'task.edit',
  'task.assign',
  'task.delete',
  'version.manage',
] as const;
export type Ability = (typeof ABILITIES)[number];

export const BASE_LAYOUT: MemberLayout = {
  landing: 'board',
  boardField: 'all',
  boardColumns: ['todo', 'in_progress', 'blocked', 'done'],
  density: 'comfortable',
};

/** What a member with no stored permissions gets — the server's role default. */
export const MEMBER_DEFAULT_ACCESS: MemberAccess = {
  role: 'member',
  sections: ['board', 'activity', 'releases'],
  abilities: ['task.create', 'task.claim', 'task.edit'],
};

const ADMIN_ACCESS: MemberAccess = {
  role: 'admin',
  sections: [...SECTIONS],
  abilities: [...ABILITIES],
};

/**
 * Access to fall back on before the first response has arrived — opening from a
 * cold cache, or offline. Deliberately the role's default rather than "nothing":
 * an empty set would blank the navigation for a moment on every load, and the
 * server rejects anything this guess gets wrong.
 */
export function assumedAccess(role: MemberAccess['role'] | null): MemberAccess {
  if (role === 'owner' || role === 'admin') return { ...ADMIN_ACCESS, role };
  return MEMBER_DEFAULT_ACCESS;
}

export function can(access: MemberAccess | null, ability: Ability): boolean {
  return access?.abilities.includes(ability) ?? false;
}

export function canSee(access: MemberAccess | null, section: Section): boolean {
  return access?.sections.includes(section) ?? false;
}

/** Human labels for the admin's permission editor. */
export const SECTION_LABELS: Record<Section, { title: string; hint: string }> = {
  board: { title: 'Board', hint: 'The task board for the fields they are in' },
  activity: { title: 'Activity', hint: 'Who finished what, and how much' },
  releases: { title: 'Releases', hint: 'Version history and changelogs' },
};

export const ABILITY_LABELS: Record<Ability, { title: string; hint: string }> = {
  'task.create': { title: 'Create tasks', hint: 'File new work in their fields' },
  'task.claim': {
    title: 'Work on tasks',
    hint: 'Take a task, move it between columns, tick off its steps',
  },
  'task.edit': { title: 'Edit tasks', hint: 'Reword a task, repriorise it, change its steps' },
  'task.assign': { title: 'Assign to others', hint: 'Put work on a colleague, not just themselves' },
  'task.delete': { title: 'Delete tasks', hint: 'Remove a task that has not been released' },
  'version.manage': { title: 'Manage releases', hint: 'Cut a version and publish its changelog' },
};

export const COLUMN_LABELS: Record<TaskStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};
